/**
 * Sección B de la trazabilidad: log crudo de los servicios que persisten app_logs en Mongo,
 * unificado en una sola línea de tiempo. Colapsado por defecto porque es material de depuración,
 * no de monitoreo diario.
 */
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TraceLogEntry } from '@/api/types';

type LogLevelFilter = 'all' | 'warning' | 'error';

/** Niveles de Serilog que cada filtro deja pasar. */
const LEVELS_BY_FILTER: Record<LogLevelFilter, readonly string[] | null> = {
  all: null,
  warning: ['Warning', 'Error', 'Fatal'],
  error: ['Error', 'Fatal'],
};

const LOGS_COL_LEVEL_KEY = 'trace.logsColLevel';

function formatTime(timestamp: string | null): string {
  if (!timestamp) return '—';
  return new Date(timestamp).toLocaleString();
}

export function TraceRawLogs({ entries }: { entries: TraceLogEntry[] }) {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(false);
  const [levelFilter, setLevelFilter] = useState<LogLevelFilter>('all');

  const visibleEntries = useMemo(() => {
    const allowedLevels = LEVELS_BY_FILTER[levelFilter];
    if (allowedLevels === null) return entries;
    return entries.filter((entry) => entry.level !== null
      && allowedLevels.includes(entry.level));
  }, [entries, levelFilter]);

  return (
    <section className="trc-logs">
      <button
        className="trc-logs-toggle"
        type="button"
        onClick={() => setIsExpanded((expanded) => !expanded)}
        aria-expanded={isExpanded}
      >
        <i className={`fas fa-chevron-${isExpanded ? 'up' : 'down'}`} aria-hidden="true" />
        <span className="trc-section-title">{t('trace.logsTitle')}</span>
      </button>

      {isExpanded && (
        <>
          <p className="trc-logs-subtitle">{t('trace.logsSubtitle')}</p>

          <label className="trc-logs-filter">
            {t(LOGS_COL_LEVEL_KEY)}
            <select className="field"
              value={levelFilter}
              onChange={(event) => setLevelFilter(event.target.value as LogLevelFilter)}
            >
              <option value="all">{t('trace.logsFilterAll')}</option>
              <option value="warning">{t('trace.logsFilterWarning')}</option>
              <option value="error">{t('trace.logsFilterError')}</option>
            </select>
          </label>

          {visibleEntries.length === 0 ? (
            <p className="trc-logs-empty">{t('trace.logsEmpty')}</p>
          ) : (
            <div className="trc-table-wrap">
              <table className="trc-table trc-logs-table">
                <thead>
                  <tr>
                    <th>{t('trace.logsColTime')}</th>
                    <th>{t('trace.logsColService')}</th>
                    <th>{t(LOGS_COL_LEVEL_KEY)}</th>
                    <th>{t('trace.logsColMessage')}</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleEntries.map((entry, position) => (
                    <tr
                      key={`${entry.service}-${entry.timestamp}-${position}`}
                      className={`trc-log-row trc-log-row--${(entry.level ?? 'unknown').toLowerCase()}`}
                    >
                      <td data-label={t('trace.logsColTime')}>{formatTime(entry.timestamp)}</td>
                      <td data-label={t('trace.logsColService')}>{entry.service}</td>
                      <td data-label={t(LOGS_COL_LEVEL_KEY)}>{entry.level ?? '—'}</td>
                      <td data-label={t('trace.logsColMessage')}>
                        {/* Envuelto en un solo span: en la tarjeta apilada móvil, la celda es
                            un flex row de dos puntas (label ← → valor). Sin este contenedor,
                            el mensaje y el <details> serían dos flex-items sueltos y
                            justify-content:space-between los separaría en vez de mostrarlos
                            como un único bloque de valor. */}
                        <span className="trc-log-message">
                          {entry.message}
                          {entry.exception && (
                            <details className="trc-log-exception">
                              <summary>{entry.exception.split('\n')[0]}</summary>
                              <pre>{entry.exception}</pre>
                            </details>
                          )}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </section>
  );
}
